import { axiosInstance } from "../../App";
import { serializeRepeatedParams } from '../../services/serializeRepeatedParams';
import {
    AddAdministrativeChargeRequestDTO,
    AddManualAdjustmentRequestDTO,
    HonorariumManualAdjustmentDTO,
    AddPaymentOrderRequestDTO,
    CreateLiquidationRequestDTO,
    HonorariumAdministrativeChargeDTO,
    HonorariumLiquidationAccountCreditDTO,
    HonorariumLiquidationDTO,
    HonorariumLiquidationDetailResponseDTO,
    HonorariumLiquidationPaymentDTO,
    HonorariumLiquidationPaymentOrderDTO,
    HonorariumLiquidationProfessionalDTO,
    HonorariumLiquidationProfessionalsTotalsDTO,
    AccountDebtLineDTO,
    PendingRetentionSummaryDTO,
    RemoveLiquidationItemResultDTO,
    RetentionLineDTO,
    UpdateLiquidationRequestDTO,
} from "../../dto/HonorariumLiquidationDTO";
import { ReceiptFormsDTO } from "../../dto/ReceiptFormsDTO";

const BASE = '/api/secured/honorariumliquidation';

/** Filtros del listado: el período se cruza con el de cada liquidación (solapamiento). */
export interface LiquidationFilters {
    fromDate?: string;
    toDate?: string;
    /** OPEN o CLOSED. */
    status?: string;
}

export function createLiquidation(body: CreateLiquidationRequestDTO): Promise<HonorariumLiquidationDTO> {
    return axiosInstance.post(`${BASE}/createLiquidation`, body).then(r => r.data);
}

export function updateLiquidation(body: UpdateLiquidationRequestDTO): Promise<HonorariumLiquidationDTO> {
    return axiosInstance.put(`${BASE}/updateLiquidation`, body).then(r => r.data);
}

export function closeLiquidation(liquidationId: number): Promise<HonorariumLiquidationDTO> {
    return axiosInstance.post(`${BASE}/closeLiquidation`, { liquidationId }).then(r => r.data);
}

export function getLiquidations(offSet: number, pageSize: number, params?: LiquidationFilters): Promise<HonorariumLiquidationDTO[]> {
    return axiosInstance.get(`${BASE}/getLiquidations`, {
        params: {
            offset: offSet,
            pageSize,
            fromDate: params?.fromDate,
            toDate: params?.toDate,
            status: params?.status,
        },
    }).then(r => r.data ?? []);
}

/** Cabecera de una liquidación, con el mismo formato que el listado. */
export function getLiquidation(liquidationId: number): Promise<HonorariumLiquidationDTO> {
    return axiosInstance.get(`${BASE}/getLiquidation`, {
        params: { liquidationId },
    }).then(r => r.data);
}

export function getLiquidationProfessionals(liquidationId: number): Promise<HonorariumLiquidationProfessionalDTO[]> {
    return axiosInstance.get(`${BASE}/getLiquidationProfessionals`, {
        params: { liquidationId },
    }).then(r => r.data ?? []);
}

/** Un profesional de la liquidación con sus montos actualizados. */
export function getLiquidationProfessional(liquidationProfessionalId: number): Promise<HonorariumLiquidationProfessionalDTO> {
    return axiosInstance.get(`${BASE}/getLiquidationProfessional`, {
        params: { liquidationProfessionalId },
    }).then(r => r.data);
}

export function getLiquidationProfessionalsTotals(liquidationId: number): Promise<HonorariumLiquidationProfessionalsTotalsDTO> {
    return axiosInstance.get(`${BASE}/getLiquidationProfessionalsTotals`, {
        params: { liquidationId },
    }).then(r => r.data);
}

/** Honorarios agrupados por origen y retenciones agrupadas por impuesto. */
export function getLiquidationDetails(liquidationProfessionalId: number): Promise<HonorariumLiquidationDetailResponseDTO> {
    return axiosInstance.get(`${BASE}/getLiquidationDetails`, {
        params: { liquidationProfessionalId },
    }).then(r => r.data);
}

/** Deducciones del profesional: anticipos históricos y deducciones de cuenta corriente. */
export function getLiquidationPayments(liquidationProfessionalId: number): Promise<HonorariumLiquidationPaymentDTO[]> {
    return axiosInstance.get(`${BASE}/getLiquidationPayments`, {
        params: { liquidationProfessionalId },
    }).then(r => r.data ?? []);
}

export function removeItemFromLiquidation(detailId: number): Promise<RemoveLiquidationItemResultDTO> {
    return axiosInstance.delete(`${BASE}/removeItemFromLiquidation`, {
        params: { detailId },
    }).then(r => r.data);
}

/** Pago al profesional: orden de pago numerada y, si el sitio maneja cajas, egreso de la caja abierta. */
export function addPaymentOrder(body: AddPaymentOrderRequestDTO): Promise<HonorariumLiquidationPaymentOrderDTO> {
    return axiosInstance.post(`${BASE}/addPaymentOrder`, body).then(r => r.data);
}

export function getLiquidationPaymentOrders(liquidationProfessionalId: number): Promise<HonorariumLiquidationPaymentOrderDTO[]> {
    return axiosInstance.get(`${BASE}/getPaymentOrders`, {
        params: { liquidationProfessionalId },
    }).then(r => r.data ?? []);
}

/** Comprobantes pendientes que forman el saldo a favor del profesional, leídos en vivo de la cuenta corriente. */
export function getAccountCredits(liquidationProfessionalId: number): Promise<HonorariumLiquidationAccountCreditDTO[]> {
    return axiosInstance.get(`${BASE}/getAccountCredits`, {
        params: { liquidationProfessionalId },
    }).then(r => r.data ?? []);
}

/** Comprobante interno del profesional y todos sus pagos: la misma fuente que la ventana de la rendición. */
export function getLiquidationReceiptForms(liquidationProfessionalId: number): Promise<ReceiptFormsDTO> {
    return axiosInstance.get(`${BASE}/getLiquidationReceiptForms`, {
        params: { liquidationProfessionalId },
    }).then(r => r.data);
}

export function getAdministrativeCharges(liquidationProfessionalId: number): Promise<HonorariumAdministrativeChargeDTO[]> {
    return axiosInstance.get(`${BASE}/getAdministrativeCharges`, {
        params: { liquidationProfessionalId },
    }).then(r => r.data ?? []);
}

export function addAdministrativeCharge(body: AddAdministrativeChargeRequestDTO): Promise<HonorariumAdministrativeChargeDTO> {
    return axiosInstance.post(`${BASE}/addAdministrativeCharge`, body).then(r => r.data);
}

export function removeAdministrativeCharge(detailId: number): Promise<void> {
    return axiosInstance.delete(`${BASE}/removeAdministrativeCharge`, {
        params: { detailId },
    }).then(r => r.data);
}

/** Débitos y créditos manuales del profesional en la liquidación. */
export function getManualAdjustments(liquidationProfessionalId: number): Promise<HonorariumManualAdjustmentDTO[]> {
    return axiosInstance.get(`${BASE}/getManualAdjustments`, {
        params: { liquidationProfessionalId },
    }).then(r => r.data ?? []);
}

/** Débito o crédito manual: solo con la liquidación abierta; lo toma el cierre. */
export function addManualAdjustment(body: AddManualAdjustmentRequestDTO): Promise<HonorariumManualAdjustmentDTO> {
    return axiosInstance.post(`${BASE}/addManualAdjustment`, body).then(r => r.data);
}

export function removeManualAdjustment(detailId: number): Promise<void> {
    return axiosInstance.delete(`${BASE}/removeManualAdjustment`, {
        params: { detailId },
    }).then(r => r.data);
}

/**
 * Profesionales con retenciones por facturación propia pendientes: entran a la liquidación aunque no tengan ítems.
 * Vacío si la entidad no es la que retiene en la sede (la retención es a favor de esa empresa).
 */
export function getPendingRetentionSummary(billingEntityId: number): Promise<PendingRetentionSummaryDTO[]> {
    return axiosInstance.get(`${BASE}/getPendingRetentionSummary`, {
        params: { billingEntityId },
    }).then(r => r.data ?? []);
}

/** Deudas de cuenta corriente que el cierre le va a descontar al profesional, con su origen (solo abierta). */
export function getAccountDebts(liquidationProfessionalId: number): Promise<AccountDebtLineDTO[]> {
    return axiosInstance.get(`${BASE}/getAccountDebts`, {
        params: { liquidationProfessionalId },
    }).then(r => r.data ?? []);
}

/** Movimientos que se le retienen al profesional por las órdenes que facturó él. */
export function getRetentions(liquidationProfessionalId: number): Promise<RetentionLineDTO[]> {
    return axiosInstance.get(`${BASE}/getRetentions`, {
        params: { liquidationProfessionalId },
    }).then(r => r.data ?? []);
}

/** Quita las retenciones del profesional (solo con la liquidación abierta): vuelven a quedar pendientes. */
export function removeRetentions(liquidationProfessionalId: number): Promise<RemoveLiquidationItemResultDTO> {
    return axiosInstance.delete(`${BASE}/removeRetentions`, {
        params: { liquidationProfessionalId },
    }).then(r => r.data);
}

// ── FINANCER (Obra Social) ──

interface FinancerPendingQueryParams {
    fromDatetime: string;
    toDatetime: string;
    professionalId: number;
    billingEntityId: number;
    healthEntityId: number;
    formStateTypes?: number[];
    benefitOrderStates?: number[];
    paymentMethodIds?: number[];
    payerTypes?: number[];
    offset?: number;
    limit?: number;
}

/** Resumen paginado por profesional. `professionalId: -1` trae todos. */
export function getFinancerPendingProfessionals(params: FinancerPendingQueryParams) {
    return axiosInstance.get(`${BASE}/getFinancerPendingProfessionals`, {
        params,
        paramsSerializer: serializeRepeatedParams,
    }).then(r => r.data);
}

export function getFinancerPendingDetails(params: FinancerPendingQueryParams) {
    return axiosInstance.get(`${BASE}/getFinancerPendingDetails`, {
        params,
        paramsSerializer: serializeRepeatedParams,
    }).then(r => r.data);
}
